import { UrlHelper } from 'utilities/UrlHelper/UrlHelper';
import amountFormat from 'utils/amount-format';

import { getBoundBillingActions } from 'reactApp/modules/BillingModule/BillingModule';
import { BoundCurrentUserSelectors } from 'reactApp/modules/CurrentUserModule';
// eslint-disable-next-line import/no-unresolved
import RPCModel from 'RPCModel';

const billingModelFactory = {
    createModelInstanceById,
    fetchModelInstanceById,
};

export { billingModelFactory };

function createModelInstanceById(projectId) {
    return new BillingModel({ [ID_FIELD]: projectId });
}

function fetchModelInstanceById(projectId) {
    return BillingModel.findOne({ [ID_FIELD]: projectId });
}

const ID_FIELD = 'pid';

const BillingModel = RPCModel.extend({
    className: 'MCS.Billing.Balance',

    idAttr: ID_FIELD,

    findOneUrl(params) {
        const { pid: targetPid } = params;
        const currentUserPid = BoundCurrentUserSelectors.getPid();
        delete params.pid;

        const isCurrentProjectTarget = currentUserPid === targetPid;

        if (isCurrentProjectTarget) {
            return `projects/${currentUserPid}/billing`;
        }
        return `partners/${currentUserPid}/clients/${targetPid}/billing`;
    },
    payUrl: 'projects/:pid/billing/card/pay',
    invoiceUrl: 'projects/:pid/billing/invoice',
    extraUrl: 'projects/:pid/billing/extra',

    defaults: {
        id: undefined,
        balance: 0,
        bonus: 0,
        currency: 'rur',
        autopay: false,
        legal_form: null,
        postpaid: false,
        extra: {
            sum4payment: undefined,
            limit2life: undefined,
            reg_notified: false,
            first_pay_dmr: 0,
            first_pay_order: 0,
        },
    },

    getInvoice(amount) {
        amount = (amount * 1).toFixed(2);

        const pid = this.get('id');

        return this.queue((model) => {
            return model.request
                .get(BillingModel.url('invoice', { pid }), {
                    amount,
                })
                .then((res) => {
                    return res.body;
                });
        });
    },

    saveExtra(key, value) {
        const pid = this.get('id');

        return this.queue((model) => {
            return model.request
                .post(BillingModel.url('extra', { pid }), {
                    [key]: value,
                })
                .then((res) => {
                    model.set('extra', res.body);

                    return res.body;
                });
        });
    },

    parse({ bonus, balance, ...rest }) {
        return {
            bonus: Number(bonus),
            balance: Number(balance),
            ...rest,
        };
    },

    getBalance() {
        return this._format(this.get('balance'));
    },

    getBonus() {
        return this._format(this.get('bonus'));
    },

    getTotalBalanceString() {
        return this._format(this.getTotalBalance());
    },

    getTotalBalance() {
        return this.get('bonus') + this.get('balance');
    },

    getPostpaid() {
        return this.get('postpaid');
    },

    _format(amount) {
        return amountFormat(amount);
    },

    refresh(time) {
        const refreshTime = time || 60 * 1000;
        const refreshPeriod = 10 * 1000;
        const balance = this.getTotalBalance();
        const startDate = new Date().getTime();

        return new Promise((resolve) => {
            const checker = function() {
                if (this.getTotalBalance() > balance) {
                    resolve('increase');
                } else if (this.getTotalBalance() < balance) {
                    resolve('decrease');
                } else if (startDate + refreshTime < new Date().getTime()) {
                    resolve(false);
                } else {
                    setTimeout(refresh, refreshPeriod);
                }
            }.bind(this);

            const refresh = function() {
                this.expire()
                    .fetch()
                    .then(checker)
                    .catch(checker);
            }.bind(this);

            refresh();
        });
    },

    getPaymentGateURL(amount) {
        amount = (amount * 1).toFixed(2);

        const pid = this.get('id');

        return BillingModel.fn.request
            .post(BillingModel.url('pay', { pid }), {
                amount,
                description: 'Пополнить счет MCS на ' + amount + ' руб.',
            })
            .then((res) => {
                return res.body.url;
            });
    },
});

BillingModel.url = UrlHelper.getModelUrlPidInjector();

BillingModel.findOne = async function({ pid }) {
    const currentUserPid = BoundCurrentUserSelectors.getPid();
    const isCurrentUserPid = currentUserPid === pid;
    const clientPid = isCurrentUserPid ? undefined : pid;

    const attrs = await getBoundBillingActions(
        currentUserPid,
        clientPid,
    ).loadData();

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const XModel = this;
    const idAttr = XModel.fn.idAttr;
    const model = XModel.all.getSafe(attrs[idAttr]);

    model.expired = false;
    model.set(attrs, { clean: true, sync: true });

    XModel.all.set([model]);

    return model;
};
